import without from 'lodash/without';
import { createSelector } from 'reselect';
import { formValueSelector } from 'redux-form';
import { InventoryAdjustmentForm } from '../../../../constants/constant_form_names';
import {
  CLOSED,
  CLOSED_ABANDONED,
  OPEN,
  PROCESSING,
} from '../../../../constants/constant_transaction_status';
import {
  PENDING,
  PROCESSED,
} from '../../../../constants/constant_inventory_status';
import { BATCH, SERIALIZED } from '../../../../constants/constant_item_types';
import { getInventoryStatus, getTransactionStatus } from '../../../../common/selectors/selector_common';

const selector = formValueSelector(InventoryAdjustmentForm);

function getEnableItemPartQuantityPreference(state) {
  return ((state.preference || {}).settings || {}).enable_item_part_quantity;
}

function getFormId(state) {
  const { _id, id } = selector(state, '_id', 'id');
  return _id || id;
}

function getFormLocationId(state) {
  return selector(state, 'location_id');
}

function getFormRunStatus(state) {
  return selector(state, 'run_status');
}

function getFormEnterUnitCost(state) {
  return selector(state, 'enter_unit_cost');
}

function getPropViewMode(state, viewMode) {
  return viewMode;
}

function getFormCurrency(state) {
  return selector(state, 'currency') || {};
}

function getFormStatus(state) {
  return selector(state, 'status') || {};
}

function getFormReferenceId(state) {
  return selector(state, 'reference_id');
}

function getFormItemLines(state) {
  return selector(state, 'lines') || [];
}

function getFormLineByIndex(state, index) {
  return selector(state, `lines[${index}]`) || {};
}

export const hasItemWithRefID = createSelector([getFormItemLines], (lines) => {
  if (!lines.length) return false;
  return lines.some((line) => {
    if (!line) return false;
    const itemTypeId = line.item_type_id;
    return itemTypeId === BATCH || itemTypeId === SERIALIZED;
  });
});

export const hasBatchSerializedItems = createSelector(
  [getFormItemLines],
  (lines) => {
    if (!lines.length) return false;
    return lines.some((line) => {
      if (!line) return false;
      const itemTypeId = line.item_type_id;
      return itemTypeId === BATCH || itemTypeId === SERIALIZED;
    });
  },
);

export const hasOnEditModeLine = createSelector([getFormItemLines], (lines) => {
  if (!lines.length) return false;
  return lines.some((line) => {
    return (
      line && line.item && !line.quantity && !line.item_id && line.onEditMode
    );
  });
});

export const getEditModeLineIndex = createSelector(
  [getFormItemLines],
  (lines) => {
    if (!lines.length) return null;
    return lines.findIndex((line) => {
      return line && line.onEditMode;
    });
  },
);

export const hasItemWithUnitCost = createSelector(
  [getFormItemLines],
  (lines) => {
    if (!lines.length) return false;
    return lines.some((line) => {
      return line && line.unit_cost;
    });
  },
);

export const isClosed = createSelector([getFormStatus], (status) => {
  const statusCid = (status || {}).cid;
  return statusCid === CLOSED;
});

export const isClosedOrClosedAbandoned = createSelector(
  [getFormStatus],
  (status) => {
    const statusCid = (status || {}).cid;
    return statusCid === CLOSED || statusCid === CLOSED_ABANDONED;
  },
);

export const isProcessing = createSelector([getFormStatus], (status) => {
  const statusCid = (status || {}).cid;
  return statusCid === PROCESSING;
});

export const getLineItemIds = createSelector([getFormItemLines], (lines) => {
  let list = [];
  if (!lines) return list;
  lines.forEach((line, index) => {
    if ((line.item || {}).id) {
      list.push({ id: line.item.id, index });
    }
  });

  return list;
});

export const getInventoryStatusList = createSelector(
  [getInventoryStatus],
  (statusList) => statusList || [],
);

export const getTransactionStatusList = createSelector(
  [getTransactionStatus],
  (statusList) => statusList || [],
);

export const getDefaultTransactionStatus = createSelector(
  [getTransactionStatusList],
  (statusList) => statusList.find((status) => status.cid === OPEN),
);

export const getDefaultLineStatus = createSelector(
  [getInventoryStatus],
  (statusList) => statusList.find((status) => status.cid === PENDING),
);

export const getInventoryStatusProcessed = createSelector(
  [getInventoryStatus],
  (statusList) => statusList.find((status) => status.cid === PROCESSED),
);

export const getTransactionStatusClosedAbandoned = createSelector(
  [getTransactionStatus],
  (statusList) => statusList.find((status) => status.cid === CLOSED_ABANDONED),
);

export const getTransactionStatusProcessing = createSelector(
  [getTransactionStatus],
  (statusList) => statusList.find((status) => status.cid === PROCESSING),
);

const getFormBrandIds = (state) => {
  return selector(state, 'brand_ids') || [];
};

const getFormClassificationIds = (state) => {
  return selector(state, 'classification_ids') || [];
};

const getFormAlternateClassIds = (state) => {
  return selector(state, 'alternate_classification_ids') || [];
};

export const getExcludedItems = createSelector(
  [getFormItemLines, getFormLineByIndex],
  (lines, ownLine) => {
    const selectedItemIds = [];
    lines.forEach((line) => {
      const itemId = (line.item || {}).id;
      const itemTypeId = (line.item || {}).item_type;
      if ([SERIALIZED, BATCH].indexOf(itemTypeId) === -1) {
        selectedItemIds.push(itemId);
      }
    });
    const { item } = ownLine;
    const itemId = (item || {}).id;
    const excludedItemIds = without(
      selectedItemIds,
      undefined,
      null,
      '',
      itemId,
    );
    return excludedItemIds;
  },
);

export const getFilterBrands = createSelector([getFormBrandIds], (brands) => {
  return brands;
});

export const getFilterClassifications = createSelector(
  [getFormClassificationIds],
  (classifications) => {
    return classifications;
  },
);

export const getFilterAlternateClass = createSelector(
  [getFormAlternateClassIds],
  (alternateClass) => {
    return alternateClass;
  },
);

export const hasRunStatus = createSelector([getFormRunStatus], (runStatus) => {
  return Boolean(runStatus);
});

export const canEnterUnitCost = createSelector(
  [getFormEnterUnitCost],
  (enterUnitCost) => {
    return Boolean(enterUnitCost);
  },
);

export const getLocationId = createSelector(
  [getFormLocationId],
  (locationId) => locationId,
);

export const isLineBatchSerializedItem = createSelector(
  [getFormLineByIndex],
  (line) => {
    const itemTypeId = line.item_type_id;
    return [BATCH, SERIALIZED].indexOf(itemTypeId) > -1;
  },
);

export const getUnfilledLineItemIndex = createSelector(
  [getFormItemLines],
  (lines) => {
    return lines.findIndex((line) => {
      return line && !line.quantity && !(line.item || {}).id;
    });
  },
);

export const getCurrencyCode = createSelector([getFormCurrency], (currency) => {
  return (currency || {}).code || 'USD';
});

export const getCurrencySymbol = createSelector(
  [getFormCurrency],
  (currency) => {
    return (currency || {}).symbol || '$';
  },
);

export const getLineOnEditMode = createSelector(
  [getFormLineByIndex],
  (line) => {
    const { onEditMode } = line;
    return onEditMode;
  },
);

export const getLineId = createSelector([getFormLineByIndex], (line) => {
  const { line_id } = line;
  return line_id;
});

export const getLineCurrentQty = createSelector(
  [getFormLineByIndex],
  (line) => {
    const { current_quantity } = line;
    return Number(current_quantity || 0);
  },
);

export const getLineUomCurrentQty = createSelector(
  [getFormLineByIndex],
  (line) => {
    const { uom_current_quantity } = line;
    return Number(uom_current_quantity || 0);
  },
);

export const getLineItemUom = createSelector(
  [getFormLineByIndex],
  (line) => {
    const { item_uom } = line;
    return item_uom;
  },
);

export const getLineStatusName = createSelector(
  [getFormLineByIndex],
  (line) => {
    const { status } = line;
    return (status || {}).name;
  },
);

export const getLineNextStatusName = createSelector(
  [getFormLineByIndex],
  (line) => {
    const { next_status } = line;
    return (next_status || {}).name;
  },
);

export const hasLineNextStatus = createSelector(
  [getFormLineByIndex],
  (line) => {
    const { next_status } = line;
    return Boolean((next_status || {}).id);
  },
);

export const getSystemMessage = createSelector(
  [getFormLineByIndex],
  (line) => line.system_message,
);

export const isSerializedItem = createSelector([getFormLineByIndex], (line) => {
  return (line.item || {}).item_type === SERIALIZED;
});

export const showLineLoader = createSelector([getFormLineByIndex], (line) => {
  return line.isFetchingItemLocation;
});

export const viewModeForm = createSelector(
  [getFormStatus, hasRunStatus, getPropViewMode],
  (status, hasRunStatus, viewMode) => {
    return (
      [PROCESSING, CLOSED, CLOSED_ABANDONED].indexOf(status.cid) > -1 ||
      hasRunStatus ||
      viewMode
    );
  },
);

export const isExistingAdjustment = createSelector([getFormId], (id) => {
  return Boolean(id);
});

export const getReferenceId = createSelector(
  [getFormReferenceId],
  (referenceId) => {
    return referenceId;
  },
);

export const getFormLines = createSelector([getFormItemLines], (lines) => {
  return lines;
});

export const hasLines = createSelector([getFormItemLines], (lines) =>
  Boolean(lines.length),
);

export const isPartQuantity = createSelector(
  [getFormLineByIndex, getEnableItemPartQuantityPreference],
  (line, enableItemPartQuantity) => {
    const { item } = line;
    return enableItemPartQuantity && (item || {}).enable_part_quantity;
  },
);
